import { useState } from "react";
import RegisterForm from "./RegisterForm";
import RegisterListView from "./RegisterListView";

const nairanaSchema = [
  { name: "Title", label: "Item name", type: "text", required: true },
  { name: "Category", label: "Category", type: "choice", required: true, choices: ["Kitchen", "Bathroom", "Bedroom", "Living / Dining", "Laundry", "Garage / Workshop", "Outside / Pool", "Garden", "Roof / Structure", "Other"] },
  { name: "Location", label: "Location", type: "text", required: false },
  { name: "Brand", label: "Brand", type: "text", required: false },
  { name: "Model", label: "Model", type: "text", required: false },
  { name: "Serial", label: "Serial number", type: "text", required: false },
  { name: "PurchaseDate", label: "Purchase date", type: "dateTime", required: false },
  { name: "Cost", label: "Cost ($)", type: "number", required: false },
  { name: "Supplier", label: "Supplier", type: "text", required: false },
  { name: "ProviderContact", label: "Provider contact", type: "text", required: false },
  { name: "WarrantyExpiry", label: "Warranty expiry", type: "dateTime", required: false },
  { name: "ServiceInterval", label: "Service interval", type: "text", required: false },
  { name: "Notes", label: "Notes", type: "text", required: false },
];

const defenderSchema = [
  { name: "Title", label: "Item name", type: "text", required: true },
  { name: "Category", label: "Category", type: "choice", required: true, choices: ["Defender Fitout", "Camping", "Auto Electrical", "Recovery", "Navigation / Communication", "Kitchen / Cooking", "Shelter / Bedding", "Clothing / Personal", "Tools", "Safety", "Other"] },
  { name: "LocationStorage", label: "Location / storage", type: "text", required: false },
  { name: "Brand", label: "Brand", type: "text", required: false },
  { name: "Model", label: "Model", type: "text", required: false },
  { name: "Serial", label: "Serial number", type: "text", required: false },
  { name: "PurchaseDate", label: "Purchase date", type: "dateTime", required: false },
  { name: "Cost", label: "Cost ($)", type: "number", required: false },
  { name: "Supplier", label: "Supplier", type: "text", required: false },
  { name: "Notes", label: "Notes", type: "text", required: false },
];

const workshopSchema = [
  { name: "Title", label: "Item name", type: "text", required: true },
  { name: "Category", label: "Category", type: "choice", required: true, choices: ["Power Tool", "Hand Tool", "Automotive", "Garden", "Safety", "Storage / Organisation", "Measuring / Calibration", "Welding", "Other"] },
  { name: "Location", label: "Location", type: "text", required: false },
  { name: "Brand", label: "Brand", type: "text", required: false },
  { name: "Model", label: "Model", type: "text", required: false },
  { name: "Serial", label: "Serial number", type: "text", required: false },
  { name: "PurchaseDate", label: "Purchase date", type: "dateTime", required: false },
  { name: "Cost", label: "Cost ($)", type: "number", required: false },
  { name: "Supplier", label: "Supplier", type: "text", required: false },
  { name: "Condition", label: "Condition", type: "choice", required: false, choices: ["Excellent", "Good", "Fair", "Poor"] },
  { name: "Notes", label: "Notes", type: "text", required: false },
];

// Mirrors defenderSchema, repointed to RhinoMaxItems. Category is intentionally
// omitted: the Choice column still holds Defender's vehicle/recovery values and
// would reject caravan entries (caravan choices are a separate later update).
const rhinomaxSchema = [
  { name: "Title", label: "Item name", type: "text", required: true },
  { name: "LocationStorage", label: "Location / storage", type: "text", required: false },
  { name: "Brand", label: "Brand", type: "text", required: false },
  { name: "Model", label: "Model", type: "text", required: false },
  { name: "Serial", label: "Serial number", type: "text", required: false },
  { name: "PurchaseDate", label: "Purchase date", type: "dateTime", required: false },
  { name: "Cost", label: "Cost ($)", type: "number", required: false },
  { name: "Supplier", label: "Supplier", type: "text", required: false },
  { name: "Notes", label: "Notes", type: "text", required: false },
];

const registers = [
  { id: "nairana", title: "Nairana Home", listName: "NairanaItems", schema: nairanaSchema, icon: "🏠", description: "Property items and appliances" },
  { id: "defender", title: "Defender & Expedition", listName: "DefenderItems", schema: defenderSchema, icon: "🚙", description: "4x4 and camping gear" },
  { id: "workshop", title: "Workshop", listName: "WorkshopItems", schema: workshopSchema, icon: "🔧", description: "Tools and equipment" },
  { id: "rhinomax", title: "RhinoMax Caravan", listName: "RhinoMaxItems", schema: rhinomaxSchema, icon: "🚐", description: "Hybrid camper contents & warranty" },
];

export default function RegistersView() {
  // active = null  → register grid
  // active = { id, mode: "list" } → read-only items list
  // active = { id, mode: "form" } → add-item form
  const [active, setActive] = useState(null);

  if (active) {
    const reg = registers.find((r) => r.id === active.id);
    if (active.mode === "form") {
      return (
        <RegisterForm
          listName={reg.listName}
          title={reg.title}
          schema={reg.schema}
          onBack={() => setActive({ id: reg.id, mode: "list" })}
        />
      );
    }
    return (
      <RegisterListView
        listName={reg.listName}
        title={reg.title}
        schema={reg.schema}
        onBack={() => setActive(null)}
        onAdd={() => setActive({ id: reg.id, mode: "form" })}
      />
    );
  }

  return (
    <div className="grid grid-cols-1 gap-3">
      {registers.map((reg) => (
        <button
          key={reg.id}
          onClick={() => setActive({ id: reg.id, mode: "list" })}
          className="bg-white rounded-2xl border border-slate-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-5 flex items-start gap-4 hover:shadow-md hover:border-[#4A6D8C]/40 transition-all text-left w-full"
        >
          <span className="text-2xl mt-0.5">{reg.icon}</span>
          <div className="min-w-0">
            <h3 className="font-semibold text-slate-900 text-base">
              {reg.title}
            </h3>
            <p className="text-sm text-slate-500 mt-1">{reg.description}</p>
            <span className="inline-block mt-3 text-xs font-medium text-[#1B3A5C]">
              View items &rarr;
            </span>
          </div>
        </button>
      ))}
    </div>
  );
}
